import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { candidateAPI } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, currency = 'INR') => {
  if (n == null || isNaN(n)) return '—';
  if (currency === 'INR') return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
};
const pct = (n) => (isNaN(n) || !isFinite(n) ? '0' : Number(n).toFixed(1)) + '%';
const num = (v) => parseFloat(v) || 0;

// ─── India Tax Engine ─────────────────────────────────────────────────────────
// CTC = Gross Salary + Employer PF + Gratuity + Medi Claim (Employer)
// Gross Salary = CTC - Employer PF - Gratuity - Medi Claim
// Take-Home    = Gross Salary - Employee PF - Income Tax - Professional Tax
function calcIndia({ ctc, regime, includeIncomeTax, pfEmployee, pfEmployer, includeGratuity, gratuityPct, includeMediClaim, mediClaimMonthly, otherDeductions80C, other80D }) {
  const totalCTC = num(ctc);
  if (totalCTC <= 0) return null;

  // ── Employer components (part of CTC, not deducted from take-home) ──────────
  const basic      = totalCTC * 0.50;
  const hraActual  = basic * 0.50;
  const pfEmpr     = Math.min(basic * (num(pfEmployer) / 100), 21600);
  const gratuity   = includeGratuity ? basic * (num(gratuityPct) / 100) : 0;
  const mediClaim  = includeMediClaim ? num(mediClaimMonthly) * 12 : 0;

  // ── Gross Salary = CTC minus employer-side costs ───────────────────────────
  const grossSalary = totalCTC - pfEmpr - gratuity - mediClaim;
  const specialAllowance = Math.max(0, grossSalary - basic - hraActual);

  // ── Employee deductions ────────────────────────────────────────────────────
  const pfEmp = Math.min(basic * (num(pfEmployee) / 100), 21600);

  // Professional Tax — Indore (MP) slabs
  const monthlyGross = grossSalary / 12;
  const ptMonthly = monthlyGross <= 18750 ? 0
    : monthlyGross <= 25000 ? 125
    : monthlyGross <= 33333 ? 166
    : 208;
  const professionalTx = ptMonthly * 12;

  // ── Helper: apply slab tax ────────────────────────────────────────────────
  function slabTax(income, nilLimit, slabs) {
    let t = 0, rem = Math.max(0, income - nilLimit);
    for (const [limit, rate] of slabs) {
      const slice = Math.min(rem, limit);
      t += slice * rate;
      rem -= slice;
      if (rem <= 0) break;
    }
    return t;
  }

  // ── Helper: surcharge with marginal relief at each threshold ─────────────
  // Marginal relief ensures crossing a threshold doesn't cause a sudden tax cliff.
  // E.g. if taxable goes from ₹50L to ₹50L+1, extra tax ≤ ₹1.
  function calcSurcharge(baseTax, taxableIncome, isNew) {
    if (taxableIncome <= 5000000) return 0; // ≤ ₹50L: no surcharge

    // Determine which surcharge tier we're in
    let surchargeRate;
    if (taxableIncome <= 10000000) surchargeRate = 0.10;       // ₹50L–1Cr
    else if (taxableIncome <= 20000000) surchargeRate = 0.15;  // ₹1Cr–2Cr
    else if (taxableIncome <= 50000000) surchargeRate = 0.25;  // ₹2Cr–5Cr
    else surchargeRate = isNew ? 0.25 : 0.37;                  // >₹5Cr

    let surcharge = baseTax * surchargeRate;

    // Marginal relief: check at the lower boundary of the current tier
    // Thresholds: [boundary, prevSurchargeRate, thisTierRate]
    const tierBoundaries = [
      [5000000,  0,    0.10],
      [10000000, 0.10, 0.15],
      [20000000, 0.15, 0.25],
      [50000000, 0.25, isNew ? 0.25 : 0.37],
    ];

    for (const [boundary, prevRate, thisRate] of tierBoundaries) {
      if (taxableIncome <= boundary || surchargeRate !== thisRate) continue;

      // Total tax (incl. cess) at exactly the boundary, using prevRate surcharge
      const taxAtBoundary = slabTax(boundary, isNew ? 400000 : 250000,
        isNew
          ? [[400000,0.05],[400000,0.10],[400000,0.15],[400000,0.20],[400000,0.25],[Infinity,0.30]]
          : [[250000,0.05],[500000,0.20],[Infinity,0.30]]
      );
      const surchargeAtBoundary = taxAtBoundary * prevRate;
      const totalAtBoundary = (taxAtBoundary + surchargeAtBoundary) * 1.04;

      // Current total before relief
      const rawTotal = (baseTax + surcharge) * 1.04;
      // Cap: total at boundary + extra income above boundary
      const cap = totalAtBoundary + (taxableIncome - boundary);

      if (rawTotal > cap) {
        // Apply relief: effective total = cap
        const effectiveTaxAndSurcharge = cap / 1.04;
        surcharge = Math.max(0, effectiveTaxAndSurcharge - baseTax);
      }
      break; // Only one tier boundary needs checking (the current tier's lower edge)
    }

    return surcharge;
  }

  // ── Income Tax ────────────────────────────────────────────────────────────
  let taxableIncome = 0, tax = 0, surcharge = 0;

  if (includeIncomeTax !== false) {
    const isNewRegime = regime !== 'old';

    if (regime === 'new_fy26' || regime === 'new_fy27') {
      // New Regime FY 2025-26 & FY 2026-27 (same slabs, Budget 2025)
      // Standard deduction ₹75,000. No 80C/80D/PF deductions.
      // 87A rebate: taxable ≤ ₹12L → tax = 0
      // Marginal relief: for taxable just above ₹12L, tax ≤ (taxable − ₹12L)
      taxableIncome = Math.max(0, grossSalary - 75000);
      tax = slabTax(taxableIncome, 400000, [
        [400000, 0.05],   // ₹4L–8L
        [400000, 0.10],   // ₹8L–12L
        [400000, 0.15],   // ₹12L–16L
        [400000, 0.20],   // ₹16L–20L
        [400000, 0.25],   // ₹20L–24L
        [Infinity, 0.30], // above ₹24L
      ]);
      if (taxableIncome <= 1200000) {
        tax = 0; // Section 87A — full rebate up to ₹12L
      } else {
        // Marginal relief: tax must not exceed income above ₹12L
        tax = Math.min(tax, taxableIncome - 1200000);
      }

    } else if (regime === 'new_fy25') {
      // New Regime FY 2024-25
      // Standard deduction ₹75,000. 87A rebate: taxable ≤ ₹7L → tax = 0
      // Marginal relief: for taxable just above ₹7L, tax ≤ (taxable − ₹7L)
      taxableIncome = Math.max(0, grossSalary - 75000);
      tax = slabTax(taxableIncome, 300000, [
        [400000, 0.05],   // ₹3L–7L
        [300000, 0.10],   // ₹7L–10L
        [200000, 0.15],   // ₹10L–12L
        [300000, 0.20],   // ₹12L–15L
        [Infinity, 0.30], // above ₹15L
      ]);
      if (taxableIncome <= 700000) {
        tax = 0;
      } else {
        tax = Math.min(tax, taxableIncome - 700000); // marginal relief
      }

    } else {
      // Old Regime
      // Standard deduction ₹50,000.
      // 80C: user enters total 80C (PF + ELSS + PPF etc.), capped at ₹1.5L.
      // 80D: user enters health insurance premium, capped at ₹25,000.
      // 87A rebate: taxable ≤ ₹5L → tax = 0 (rebate up to ₹12,500)
      // Marginal relief: net tax ≤ (taxable − ₹5L) for income just above ₹5L
      const total80C = Math.min(num(otherDeductions80C) || 0, 150000);
      const deduction80D = Math.min(num(other80D) || 0, 25000);
      taxableIncome = Math.max(0, grossSalary - 50000 - total80C - deduction80D);
      tax = slabTax(taxableIncome, 250000, [
        [250000, 0.05],   // ₹2.5L–5L
        [500000, 0.20],   // ₹5L–10L
        [Infinity, 0.30], // above ₹10L
      ]);
      if (taxableIncome <= 500000) {
        tax = 0; // 87A rebate covers full tax (max ₹12,500)
      } else {
        // Marginal relief: net tax ≤ (taxable − ₹5L)
        tax = Math.min(tax, taxableIncome - 500000);
      }
    }

    surcharge = calcSurcharge(tax, taxableIncome, isNewRegime);
  } else {
    // Income tax excluded — show taxable income for reference only
    const stdDeduction = regime === 'old' ? 50000 : 75000;
    taxableIncome = Math.max(0, grossSalary - stdDeduction);
  }

  const cess     = (tax + surcharge) * 0.04;
  const totalTax = tax + surcharge + cess;
  const takeHome = grossSalary - pfEmp - totalTax - professionalTx;

  const deductionsBreakdown = [
    { label: 'PF Employee', amount: pfEmp },
    ...(includeIncomeTax !== false ? [
      { label: 'Income Tax', amount: tax },
      ...(surcharge > 0 ? [{ label: 'Surcharge', amount: surcharge }] : []),
      { label: 'Education Cess (4%)', amount: cess },
    ] : []),
    { label: 'Professional Tax', amount: professionalTx },
  ];

  const ctcBreakdown = [
    { label: 'Basic (50% of CTC)', amount: basic },
    { label: 'HRA (50% of Basic)', amount: hraActual },
    { label: 'Special Allowance', amount: specialAllowance },
    { label: 'PF Employer (12%)', amount: pfEmpr },
    { label: `Gratuity (${num(gratuityPct)}% of Basic)`, amount: gratuity },
    ...(mediClaim > 0 ? [{ label: `Medi Claim Employer (₹${num(mediClaimMonthly)}/mo)`, amount: mediClaim }] : []),
  ];

  return {
    totalCTC, grossSalary, basic, hraActual, specialAllowance,
    pfEmployee: pfEmp, pfEmployer: pfEmpr, gratuity, mediClaim,
    taxableIncome, tax, surcharge, cess, totalTax,
    professionalTax: professionalTx,
    takeHome: Math.max(0, takeHome),
    inHandMonthly: Math.max(0, takeHome / 12),
    regime: regime === 'old' ? 'Old Regime' : 'New Regime',
    deductionsBreakdown,
    ctcBreakdown,
  };
}

// ─── USA Tax Engine ───────────────────────────────────────────────────────────
function calcUSA({ salary, filingStatus, stateTaxPct, k401Pct, healthInsurance }) {
  const gross = num(salary);
  if (gross <= 0) return null;

  const k401 = Math.min(gross * (num(k401Pct) / 100), 23000); // 2024 limit
  const healthIns = num(healthInsurance);
  const stdDeduction = filingStatus === 'married' ? 29200 : 14600;
  const taxable = Math.max(0, gross - k401 - healthIns - stdDeduction);

  // Federal brackets 2024 (single / married)
  const brackets = filingStatus === 'married'
    ? [[23200,0.10],[94300,0.12],[201050,0.22],[383900,0.24],[487450,0.32],[731200,0.35],[Infinity,0.37]]
    : [[11600,0.10],[47150,0.12],[100525,0.22],[191950,0.24],[243725,0.32],[609350,0.35],[Infinity,0.37]];

  let federalTax = 0, rem = taxable;
  let prev = 0;
  for (const [limit, rate] of brackets) {
    const slice = Math.min(rem, limit - prev);
    if (slice <= 0) break;
    federalTax += slice * rate;
    rem -= slice;
    prev = limit;
    if (rem <= 0) break;
  }

  // FICA
  const ssTaxable = Math.min(gross, 168600);
  const socialSecurity = ssTaxable * 0.062;
  const medicare = gross * 0.0145;
  const fica = socialSecurity + medicare;

  const stateTax = gross * (num(stateTaxPct) / 100);
  const totalTax = federalTax + fica + stateTax;
  const takeHome = gross - k401 - healthIns - totalTax;

  return {
    gross, k401, healthInsurance: healthIns,
    federalTax, socialSecurity, medicare, fica, stateTax, totalTax,
    takeHome: Math.max(0, takeHome),
    inHandMonthly: Math.max(0, takeHome / 12),
    deductionsBreakdown: [
      { label: '401(k) Contribution', amount: k401 },
      { label: 'Health Insurance', amount: healthIns },
      { label: 'Federal Income Tax', amount: federalTax },
      { label: 'Social Security (6.2%)', amount: socialSecurity },
      { label: 'Medicare (1.45%)', amount: medicare },
      { label: `State Tax (${stateTaxPct}%)`, amount: stateTax },
    ],
  };
}

// ─── Billing Margin Calculator ────────────────────────────────────────────────
function calcMargin({ candidateCTC, billingRate, currency, workingDays, hoursPerDay }) {
  const ctc   = num(candidateCTC);
  const rate  = num(billingRate);
  if (ctc <= 0 || rate <= 0) return null;

  let billingAnnual;
  if (currency === 'INR') {
    // billing rate is monthly in INR
    billingAnnual = rate * 12;
  } else {
    // billing rate is hourly in USD
    const days = num(workingDays) || 260;
    const hrs  = num(hoursPerDay) || 8;
    billingAnnual = rate * days * hrs;
  }

  const grossMargin   = billingAnnual - ctc;
  const marginPct     = (grossMargin / billingAnnual) * 100;
  const markupPct     = (grossMargin / ctc) * 100;
  const breakEvenRate = currency === 'INR' ? ctc / 12 : ctc / ((num(workingDays)||260) * (num(hoursPerDay)||8));

  return {
    ctc, billingAnnual, grossMargin, marginPct, markupPct, breakEvenRate,
    billingMonthly: currency === 'INR' ? rate : billingAnnual / 12,
    ctcMonthly: ctc / 12,
  };
}

// ─── UI Sub-components ────────────────────────────────────────────────────────
function FieldRow({ label, children, isDark }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, prefix, suffix, isDark, placeholder = '0', step = '1000' }) {
  const inp = {
    width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 14, fontWeight: 500,
    border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
    background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: isDark ? '#f1f5f9' : '#0f172a', outline: 'none',
    paddingLeft: prefix ? 28 : 12, paddingRight: suffix ? 36 : 12,
  };
  return (
    <div style={{ position: 'relative' }}>
      {prefix && <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: isDark ? '#94a3b8' : '#6b7280', fontWeight: 600 }}>{prefix}</span>}
      <input type="number" style={inp} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} step={step} min="0" />
      {suffix && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: isDark ? '#64748b' : '#9ca3af' }}>{suffix}</span>}
    </div>
  );
}

function Sel({ value, onChange, options, isDark }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
        border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
        background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
        color: isDark ? '#f1f5f9' : '#0f172a', outline: 'none', cursor: 'pointer',
      }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Toggle({ checked, onChange, isDark }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 11, cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
        background: checked ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'),
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3, width: 16, height: 16,
        borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

function ResultCard({ label, value, sub, accent, isDark }) {
  return (
    <div style={{
      background: accent
        ? `linear-gradient(135deg, ${isDark ? '#1e1b4b' : '#eef2ff'}, ${isDark ? '#172554' : '#dbeafe'})`
        : (isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc'),
      border: `1.5px solid ${accent ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0')}`,
      borderRadius: 12, padding: '16px 20px',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: accent ? 26 : 20, fontWeight: 700, color: accent ? '#6366f1' : (isDark ? '#f1f5f9' : '#0f172a') }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: isDark ? '#64748b' : '#9ca3af', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function BreakdownBar({ items, total, currency, isDark }) {
  const colors = ['#6366f1', '#22d3ee', '#f59e0b', '#10b981', '#f43f5e', '#8b5cf6', '#06b6d4'];
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
        {items.map((item, i) => (
          <div key={i} style={{ width: `${(item.amount / total) * 100}%`, background: colors[i % colors.length], transition: 'width 0.4s' }} title={item.label} />
        ))}
      </div>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: colors[i % colors.length], flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: isDark ? '#cbd5e1' : '#374151' }}>{item.label}</span>
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#f1f5f9' : '#111827' }}>{fmt(item.amount, currency)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Candidate Search Dropdown ────────────────────────────────────────────────
function CandidateSearch({ onSelect, isDark, label = 'Load from candidate' }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await candidateAPI.getAll({ search: query, limit: 8 });
        setResults(res.data?.candidates || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const anyOverlayOpen = open;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);

  return (
    <div style={{ position: 'relative' }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search candidate name or email…"
          style={{
            width: '100%', padding: '9px 12px', paddingRight: 36, borderRadius: 8, fontSize: 13,
            border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
            background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
            color: isDark ? '#f1f5f9' : '#0f172a', outline: 'none', boxSizing: 'border-box',
          }}
        />
        {loading && <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, border: '2px solid #6366f1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />}
      </div>
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, marginTop: 4,
          background: isDark ? '#1e293b' : '#fff', borderRadius: 10,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 240, overflowY: 'auto',
        }}>
          {results.map(c => (
            <div
              key={c.id}
              onClick={() => { onSelect(c); setQuery(c.fullName); setOpen(false); setResults([]); }}
              style={{
                padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`,
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(99,102,241,0.15)' : '#f0f4ff'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#f1f5f9' : '#111827', wordBreak: 'break-word' }}>{c.fullName}</div>
              <div style={{ fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{c.email} · {c.currentRole || 'N/A'} · {c.experience} yrs</div>
              {c.expectedSalary && <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600 }}>{fmt(c.expectedSalary, c.expectedSalaryCurrency === 'USD' ? 'USD' : 'INR')}</div>}
            </div>
          ))}
        </div>
      )}
      {open && <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpen(false)} />}
    </div>
  );
}

// ─── Professional Tax Slab Table (Indore, MP) ─────────────────────────────────
function PTSlabTable({ result, isDark }) {
  const slabs = [
    { range: '₹1 – ₹18,750',       monthly: '₹0',   annual: '₹0',     max: 18750,   rate: 0   },
    { range: '₹18,751 – ₹25,000',  monthly: '₹125', annual: '₹1,500', max: 25000,   rate: 125 },
    { range: '₹25,001 – ₹33,333',  monthly: '₹166', annual: '₹1,992', max: 33333,   rate: 166 },
    { range: '₹33,334 and above',  monthly: '₹208', annual: '₹2,496', max: Infinity, rate: 208 },
  ];

  const monthlyGross = result ? result.grossSalary / 12 : null;
  const activeIdx = monthlyGross != null
    ? slabs.findIndex((s, i) => {
        const prevMax = i === 0 ? 0 : slabs[i - 1].max;
        return monthlyGross > prevMax && monthlyGross <= s.max;
      })
    : -1;

  const activePT = activeIdx >= 0 ? slabs[activeIdx].rate : null;

  const border = isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0';
  const headerBg = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9';
  const activeBg = isDark ? 'rgba(99,102,241,0.18)' : '#eef2ff';
  const activeBorder = '#6366f1';

  return (
    <div style={{ marginTop: 18, borderRadius: 10, overflow: 'hidden', border: `1px solid ${border}` }}>
      <div style={{ overflowX: 'auto' }}>
      {/* Header */}
      <div style={{ padding: '8px 14px', background: headerBg, borderBottom: `1px solid ${border}`, minWidth: 380 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Professional Tax Slabs — Indore (MP)
        </span>
        <span style={{ fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', marginLeft: 8 }}>
          Effective 01/07/2023 · Monthly deduction cycle
        </span>
      </div>

      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: headerBg, borderBottom: `1px solid ${border}`, padding: '6px 14px', minWidth: 380 }}>
        {['Monthly Gross Salary', 'Monthly Tax', 'Annual Tax'].map(h => (
          <span key={h} style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#64748b' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</span>
        ))}
      </div>

      {/* Rows */}
      {slabs.map((slab, i) => {
        const isActive = i === activeIdx;
        return (
          <div
            key={i}
            style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              padding: '8px 14px',
              background: isActive ? activeBg : 'transparent',
              borderLeft: isActive ? `3px solid ${activeBorder}` : '3px solid transparent',
              borderBottom: i < slabs.length - 1 ? `1px solid ${border}` : 'none',
              transition: 'background 0.2s',
              minWidth: 380,
            }}
          >
            <span style={{ fontSize: 12, color: isDark ? '#cbd5e1' : '#374151', fontWeight: isActive ? 600 : 400, whiteSpace: 'nowrap' }}>{slab.range}</span>
            <span style={{ fontSize: 12, color: isActive ? '#6366f1' : (isDark ? '#cbd5e1' : '#374151'), fontWeight: isActive ? 700 : 400 }}>{slab.monthly}</span>
            <span style={{ fontSize: 12, color: isActive ? '#6366f1' : (isDark ? '#cbd5e1' : '#374151'), fontWeight: isActive ? 700 : 400 }}>{slab.annual}</span>
          </div>
        );
      })}

      {activeIdx >= 0 && (
        <div style={{ padding: '6px 14px', background: isDark ? 'rgba(99,102,241,0.1)' : '#f0f4ff', borderTop: `1px solid ${border}`, minWidth: 380 }}>
          <span style={{ fontSize: 11, color: isDark ? '#a5b4fc' : '#4338ca', fontWeight: 600 }}>
            ✦ Monthly gross ₹{Math.round(monthlyGross).toLocaleString('en-IN')} → PT deduction ₹{activePT}/month
          </span>
        </div>
      )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Take-Home Calculator
// ══════════════════════════════════════════════════════════════════════════════
function TakeHomeTab({ country, isDark }) {
  const currency = country === 'India' ? 'INR' : 'USD';
  const [viewMode, setViewMode] = useState('annual'); // 'annual' | 'monthly'

  // India state
  const [indiaForm, setIndiaForm] = useState({
    ctc: '', regime: 'new_fy27', includeIncomeTax: true,
    pfEmployee: 12, pfEmployer: 12,
    includeGratuity: true, gratuityPct: 4.81,
    includeMediClaim: true, mediClaimMonthly: 330,
    otherDeductions80C: 0, other80D: 0,
  });
  // USA state
  const [usaForm, setUsaForm] = useState({
    salary: '', filingStatus: 'single', stateTaxPct: 5, k401Pct: 6, healthInsurance: 3000,
  });

  const result = country === 'India' ? calcIndia(indiaForm) : calcUSA(usaForm);
  const setI = (k, v) => setIndiaForm(p => ({ ...p, [k]: v }));
  const setU = (k, v) => setUsaForm(p => ({ ...p, [k]: v }));

  // Monthly/Annual display helper
  const D = (annual) => viewMode === 'monthly' ? fmt(annual / 12, currency) : fmt(annual, currency);
  const suffix = viewMode === 'monthly' ? '/mo' : '/yr';

  const handleCandidateSelect = (c) => {
    if (country === 'India') setI('ctc', c.expectedSalary || '');
    else setU('salary', c.expectedSalary || '');
    toast.success(`Loaded ${c.fullName}'s expected salary`);
  };

  const card = { background: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, borderRadius: 14, padding: 20 };

  // Monthly/Annual toggle pill
  const ViewToggle = () => (
    <div style={{ display: 'flex', background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
      {['annual', 'monthly'].map(m => (
        <button key={m} onClick={() => setViewMode(m)} style={{
          padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.2s',
          background: viewMode === m ? '#6366f1' : 'transparent',
          color: viewMode === m ? '#fff' : (isDark ? '#94a3b8' : '#6b7280'),
        }}>
          {m === 'annual' ? 'Annual' : 'Monthly'}
        </button>
      ))}
    </div>
  );

  return (
    <div className="ctc-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* ── Inputs ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            {country === 'India' ? '🇮🇳 India CTC Details' : '🇺🇸 USA Salary Details'}
          </p>
          {country === 'India' && (
            <a href="https://cleartax.in/paytax/taxcalculator" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#818cf8' : '#4f46e5', textDecoration: 'none', background: isDark ? 'rgba(99,102,241,0.15)' : '#ede9fe', padding: '3px 10px', borderRadius: 20, border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c4b5fd'}`, whiteSpace: 'nowrap' }}>
              🔗 ClearTax ↗
            </a>
          )}
        </div>
        <CandidateSearch onSelect={handleCandidateSelect} isDark={isDark} />
        <div style={{ height: 16 }} />

        {country === 'India' ? (
          <>
            <FieldRow label="Annual CTC (₹)" isDark={isDark}>
              <NumInput value={indiaForm.ctc} onChange={v => setI('ctc', v)} prefix="₹" isDark={isDark} placeholder="e.g. 1200000" />
            </FieldRow>

            {/* Info chips showing the fixed formula */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {[
                `Basic = 50% of CTC${result ? ' (₹' + Math.round(result.basic/12).toLocaleString('en-IN') + '/mo)' : ''}`,
                `HRA = 50% of Basic${result ? ' (₹' + Math.round(result.hraActual/12).toLocaleString('en-IN') + '/mo)' : ''}`,
                `Gross Salary${result ? ' = ₹' + Math.round(result.grossSalary/12).toLocaleString('en-IN') + '/mo' : ' = CTC − Employer costs'}`,
              ].map(t => (
                <span key={t} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff', color: isDark ? '#a5b4fc' : '#4338ca', fontWeight: 500 }}>{t}</span>
              ))}
            </div>

            {/* Include Income Tax toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, marginBottom: 14, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 500, color: isDark ? '#cbd5e1' : '#374151' }}>Include Income Tax</span>
                {!indiaForm.includeIncomeTax && <span style={{ fontSize: 11, marginLeft: 8, padding: '2px 7px', borderRadius: 4, background: isDark ? 'rgba(251,191,36,0.15)' : '#fef9c3', color: isDark ? '#fbbf24' : '#92400e', fontWeight: 600 }}>Excluded from calculation</span>}
              </div>
              <Toggle checked={indiaForm.includeIncomeTax} onChange={v => setI('includeIncomeTax', v)} isDark={isDark} />
            </div>

            {indiaForm.includeIncomeTax && (
              <FieldRow label="Tax Regime" isDark={isDark}>
                <Sel value={indiaForm.regime} onChange={v => setI('regime', v)} isDark={isDark} options={[
                  { value: 'new_fy27', label: '🆕 New Regime — FY 2026-27 (AY 2027-28)' },
                  { value: 'new_fy26', label: 'New Regime — FY 2025-26 (AY 2026-27)' },
                  { value: 'old',     label: 'Old Regime' },
                ]} />
              </FieldRow>
            )}
            <div className="ctc-pair" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FieldRow label="PF Employee %" isDark={isDark}>
                <NumInput value={indiaForm.pfEmployee} onChange={v => setI('pfEmployee', v)} suffix="%" isDark={isDark} step="1" placeholder="12" />
              </FieldRow>
              <FieldRow label="PF Employer %" isDark={isDark}>
                <NumInput value={indiaForm.pfEmployer} onChange={v => setI('pfEmployer', v)} suffix="%" isDark={isDark} step="1" placeholder="12" />
              </FieldRow>
            </div>
            {indiaForm.regime === 'old' && (
              <>
                <FieldRow label={`Total 80C Deductions (₹) — max ₹1,50,000${result ? ' · PF = ₹' + result.pfEmployee.toLocaleString('en-IN') : ''}`} isDark={isDark}>
                  <NumInput value={indiaForm.otherDeductions80C} onChange={v => setI('otherDeductions80C', v)} prefix="₹" isDark={isDark} placeholder="e.g. 150000 (PF + ELSS + PPF…)" />
                </FieldRow>
                <FieldRow label="80D Health Insurance (₹) — max ₹25,000" isDark={isDark}>
                  <NumInput value={indiaForm.other80D} onChange={v => setI('other80D', v)} prefix="₹" isDark={isDark} placeholder="e.g. 25000" />
                </FieldRow>
              </>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              {/* Gratuity row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexShrink: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: isDark ? '#cbd5e1' : '#374151', whiteSpace: 'nowrap' }}>Include Gratuity</span>
                  <span style={{ fontSize: 11, color: isDark ? '#64748b' : '#9ca3af' }}>(%&nbsp;of&nbsp;Basic)</span>
                  <div style={{ position: 'relative', width: 68 }}>
                    <input
                      type="number" step="0.01" min="0" max="100"
                      value={indiaForm.gratuityPct}
                      onChange={e => setI('gratuityPct', e.target.value)}
                      disabled={!indiaForm.includeGratuity}
                      style={{
                        width: '100%', padding: '3px 20px 3px 7px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`,
                        background: indiaForm.includeGratuity ? (isDark ? 'rgba(255,255,255,0.08)' : '#fff') : 'transparent',
                        color: isDark ? '#f1f5f9' : '#111827', outline: 'none',
                        opacity: indiaForm.includeGratuity ? 1 : 0.4, boxSizing: 'border-box',
                      }}
                    />
                    <span style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', pointerEvents: 'none' }}>%</span>
                  </div>
                </div>
                <Toggle checked={indiaForm.includeGratuity} onChange={v => setI('includeGratuity', v)} isDark={isDark} />
              </div>

              {/* Medi Claim row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexShrink: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: isDark ? '#cbd5e1' : '#374151', whiteSpace: 'nowrap' }}>Medi Claim (Employer)</span>
                  <span style={{ fontSize: 11, color: isDark ? '#64748b' : '#9ca3af' }}>(₹/mo)</span>
                  <div style={{ position: 'relative', width: 72 }}>
                    <span style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', pointerEvents: 'none' }}>₹</span>
                    <input
                      type="number" step="10" min="0"
                      value={indiaForm.mediClaimMonthly}
                      onChange={e => setI('mediClaimMonthly', e.target.value)}
                      disabled={!indiaForm.includeMediClaim}
                      style={{
                        width: '100%', padding: '3px 6px 3px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`,
                        background: indiaForm.includeMediClaim ? (isDark ? 'rgba(255,255,255,0.08)' : '#fff') : 'transparent',
                        color: isDark ? '#f1f5f9' : '#111827', outline: 'none',
                        opacity: indiaForm.includeMediClaim ? 1 : 0.4, boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
                <Toggle checked={indiaForm.includeMediClaim} onChange={v => setI('includeMediClaim', v)} isDark={isDark} />
              </div>
            </div>

            {/* Professional Tax Slab Reference — Indore */}
            <PTSlabTable result={result} isDark={isDark} />
          </>
        ) : (
          <>
            <FieldRow label="Annual Salary ($)" isDark={isDark}>
              <NumInput value={usaForm.salary} onChange={v => setU('salary', v)} prefix="$" isDark={isDark} placeholder="e.g. 120000" />
            </FieldRow>
            <FieldRow label="Filing Status" isDark={isDark}>
              <Sel value={usaForm.filingStatus} onChange={v => setU('filingStatus', v)} isDark={isDark} options={[{ value: 'single', label: 'Single' }, { value: 'married', label: 'Married Filing Jointly' }]} />
            </FieldRow>
            <FieldRow label="State Income Tax %" isDark={isDark}>
              <NumInput value={usaForm.stateTaxPct} onChange={v => setU('stateTaxPct', v)} suffix="%" isDark={isDark} step="0.5" placeholder="5" />
            </FieldRow>
            <FieldRow label="401(k) Contribution %" isDark={isDark}>
              <NumInput value={usaForm.k401Pct} onChange={v => setU('k401Pct', v)} suffix="%" isDark={isDark} step="1" placeholder="6" />
            </FieldRow>
            <FieldRow label="Annual Health Insurance ($)" isDark={isDark}>
              <NumInput value={usaForm.healthInsurance} onChange={v => setU('healthInsurance', v)} prefix="$" isDark={isDark} placeholder="3000" />
            </FieldRow>
          </>
        )}
      </div>

      {/* ── Results ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {result ? (
          <>
            {/* Annual / Monthly toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280' }}>View as</span>
              <ViewToggle />
            </div>

            <div className="ctc-pair" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <ResultCard
                label={`Take-Home (${viewMode === 'monthly' ? 'Monthly' : 'Annual'})`}
                value={D(result.takeHome)}
                sub={viewMode === 'monthly' ? `${fmt(result.takeHome, currency)} / year` : `${fmt(result.inHandMonthly, currency)} / month`}
                accent isDark={isDark}
              />
              <ResultCard
                label={`Gross Salary${suffix}`}
                value={D(result.grossSalary ?? result.gross)}
                sub={`CTC: ${D(result.totalCTC ?? result.gross)}`}
                isDark={isDark}
              />
              <ResultCard label={`Total Tax${suffix}`} value={D(result.totalTax)} sub={pct((result.totalTax / (result.grossSalary ?? result.gross)) * 100) + ' of gross'} isDark={isDark} />
              <ResultCard label={country === 'India' ? 'Taxable Income' : `FICA${suffix}`} value={D(country === 'India' ? result.taxableIncome : result.fica)} isDark={isDark} />
            </div>

            {/* ClearTax comparison hint */}
            {country === 'India' && result.grossSalary && (
              <div style={{ borderRadius: 8, background: isDark ? 'rgba(99,102,241,0.1)' : '#f0f4ff', border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#c7d2fe'}`, overflow: 'hidden' }}>
                <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: isDark ? '#a5b4fc' : '#4338ca' }}>🔍</span>
                  <span style={{ fontSize: 11, color: isDark ? '#a5b4fc' : '#4338ca', lineHeight: 1.5 }}>
                    <b>Comparing with ClearTax?</b> Enter <b>{fmt(result.grossSalary)}</b> as Annual Income (Gross Salary, not CTC).
                    {result.surcharge > 0 && <span style={{ opacity: 0.85 }}> Surcharge included: {fmt(result.surcharge)}.</span>}
                  </span>
                </div>
                <a
                  href="https://cleartax.in/paytax/taxcalculator"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 14px', background: isDark ? '#4f46e5' : '#4338ca', color: '#fff', fontWeight: 600, fontSize: 12, textDecoration: 'none', cursor: 'pointer' }}
                >
                  🔗 Open ClearTax Tax Calculator ↗
                </a>
              </div>
            )}

            <div style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, borderRadius: 14, padding: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>Deductions from Gross Salary ({viewMode})</p>
              <BreakdownBar
                items={result.deductionsBreakdown.map(d => ({ ...d, amount: viewMode === 'monthly' ? d.amount / 12 : d.amount }))}
                total={viewMode === 'monthly' ? (result.grossSalary ?? result.gross) / 12 : (result.grossSalary ?? result.gross)}
                currency={currency} isDark={isDark}
              />
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#22d3ee' : '#0891b2' }}>Net Take-Home</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#22d3ee' : '#0891b2' }}>{D(result.takeHome)}</span>
              </div>
            </div>

            {country === 'India' && result.ctcBreakdown && (
              <div style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, borderRadius: 14, padding: 20 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>CTC Breakdown ({viewMode})</p>
                <BreakdownBar
                  items={result.ctcBreakdown.filter(x => x.amount > 0).map(d => ({ ...d, amount: viewMode === 'monthly' ? d.amount / 12 : d.amount }))}
                  total={viewMode === 'monthly' ? result.totalCTC / 12 : result.totalCTC}
                  currency={currency} isDark={isDark}
                />
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12 }}>
            <div style={{ fontSize: 40 }}>🧮</div>
            <p style={{ fontSize: 15, color: isDark ? '#475569' : '#9ca3af', fontWeight: 500 }}>Enter a salary to see the breakdown</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Billing Margin
// ══════════════════════════════════════════════════════════════════════════════
function BillingMarginTab({ country, isDark }) {
  const currency = country === 'India' ? 'INR' : 'USD';
  const [form, setForm] = useState({ candidateCTC: '', billingRate: '', workingDays: 260, hoursPerDay: 8 });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const result = calcMargin({ ...form, currency });

  const handleCandidateSelect = (c) => {
    set('candidateCTC', c.expectedSalary || '');
    toast.success(`Loaded ${c.fullName}'s expected salary`);
  };

  const card = { background: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, borderRadius: 14, padding: 20 };
  const marginColor = result ? (result.marginPct >= 30 ? '#10b981' : result.marginPct >= 15 ? '#f59e0b' : '#f43f5e') : '#6366f1';

  return (
    <div className="ctc-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <div style={card}>
        <p style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>💼 Billing Details</p>
        <CandidateSearch onSelect={handleCandidateSelect} isDark={isDark} />
        <div style={{ height: 16 }} />
        <FieldRow label={`Candidate CTC / Salary (${currency === 'INR' ? '₹/year' : '$/year'})`} isDark={isDark}>
          <NumInput value={form.candidateCTC} onChange={v => set('candidateCTC', v)} prefix={currency === 'INR' ? '₹' : '$'} isDark={isDark} placeholder={currency === 'INR' ? '1200000' : '80000'} />
        </FieldRow>
        <FieldRow label={currency === 'INR' ? 'Billing Rate to Client (₹/month)' : 'Billing Rate to Client ($/hour)'} isDark={isDark}>
          <NumInput value={form.billingRate} onChange={v => set('billingRate', v)} prefix={currency === 'INR' ? '₹' : '$'} isDark={isDark} placeholder={currency === 'INR' ? '150000' : '60'} />
        </FieldRow>
        {currency === 'USD' && (
          <div className="ctc-pair" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FieldRow label="Working Days/Year" isDark={isDark}>
              <NumInput value={form.workingDays} onChange={v => set('workingDays', v)} isDark={isDark} step="1" />
            </FieldRow>
            <FieldRow label="Hours/Day" isDark={isDark}>
              <NumInput value={form.hoursPerDay} onChange={v => set('hoursPerDay', v)} isDark={isDark} step="1" />
            </FieldRow>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {result ? (
          <>
            <div className="ctc-pair" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <ResultCard label="Gross Margin" value={fmt(result.grossMargin, currency)} sub={pct(result.marginPct) + ' margin'} accent isDark={isDark} />
              <ResultCard label="Markup on Cost" value={pct(result.markupPct)} sub="on candidate cost" isDark={isDark} />
              <ResultCard label="Annual Billing" value={fmt(result.billingAnnual, currency)} sub={`${fmt(result.billingMonthly, currency)} / month`} isDark={isDark} />
              <ResultCard label="Candidate Cost" value={fmt(result.ctc, currency)} sub={`${fmt(result.ctcMonthly, currency)} / month`} isDark={isDark} />
            </div>

            <div style={{ ...card }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Margin Health</p>
              <div style={{ display: 'flex', height: 28, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ width: `${Math.min(100, Math.max(0, result.marginPct))}%`, background: marginColor, transition: 'width 0.5s', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{pct(result.marginPct)}</span>
                </div>
                <div style={{ flex: 1, background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }} />
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 12, flexWrap: 'wrap' }}>
                {[['< 15%', '#f43f5e', 'Low'], ['15–30%', '#f59e0b', 'Okay'], ['> 30%', '#10b981', 'Good']].map(([range, color, label]) => (
                  <div key={range} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
                    <span style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>{range} — {label}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}` }}>
                <span style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#6b7280' }}>Break-even rate: </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>
                  {fmt(result.breakEvenRate, currency)}{currency === 'USD' ? '/hr' : '/mo'}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12 }}>
            <div style={{ fontSize: 40 }}>📊</div>
            <p style={{ fontSize: 15, color: isDark ? '#475569' : '#9ca3af', fontWeight: 500 }}>Enter CTC and billing rate to calculate margin</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — CTC Comparison
// ══════════════════════════════════════════════════════════════════════════════
const EMPTY_CANDIDATE = () => ({ name: '', ctc: '', billingRate: '', role: '' });
function ComparisonTab({ country, isDark }) {
  const currency = country === 'India' ? 'INR' : 'USD';
  const [rows, setRows] = useState([EMPTY_CANDIDATE(), EMPTY_CANDIDATE(), EMPTY_CANDIDATE()]);

  const setRow = (i, k, v) => setRows(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(p => [...p, EMPTY_CANDIDATE()]);
  const removeRow = (i) => setRows(p => p.filter((_, idx) => idx !== i));

  const handleCandidateSelect = (i, c) => {
    setRows(p => p.map((r, idx) => idx === i ? {
      ...r,
      name: c.fullName,
      ctc: c.expectedSalary || '',
      role: c.currentRole || '',
    } : r));
    toast.success(`Loaded ${c.fullName}`);
  };

  const results = rows.map(r => {
    if (!num(r.ctc)) return null;
    const margin = num(r.billingRate) ? calcMargin({ candidateCTC: r.ctc, billingRate: r.billingRate, currency }) : null;
    return { ctc: num(r.ctc), billing: num(r.billingRate), margin };
  });

  const maxCTC = Math.max(...results.map(r => r?.ctc || 0));
  const inp = { padding: '7px 10px', borderRadius: 8, fontSize: 13, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', color: isDark ? '#f1f5f9' : '#0f172a', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const COLORS = ['#6366f1', '#22d3ee', '#f59e0b', '#10b981'];

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {rows.map((row, i) => (
          <div key={i} style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, borderRadius: 14, padding: 16, borderLeft: `4px solid ${COLORS[i % COLORS.length]}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: COLORS[i % COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {i + 1}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280' }}>Candidate {i + 1}</span>
              {rows.length > 2 && (
                <button onClick={() => removeRow(i)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: isDark ? '#475569' : '#d1d5db', lineHeight: 1 }}>×</button>
              )}
            </div>
            <div className="ctc-compare-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, alignItems: 'end' }}>
              <CandidateSearch onSelect={c => handleCandidateSelect(i, c)} isDark={isDark} label="Load from candidate" />
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#9ca3af', marginBottom: 5 }}>NAME (OVERRIDE)</label>
                <input style={inp} value={row.name} onChange={e => setRow(i, 'name', e.target.value)} placeholder="Override name…" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#9ca3af', marginBottom: 5 }}>CTC / SALARY</label>
                <input style={inp} type="number" value={row.ctc} onChange={e => setRow(i, 'ctc', e.target.value)} placeholder={currency === 'INR' ? '₹ per year' : '$ per year'} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#9ca3af', marginBottom: 5 }}>{currency === 'INR' ? 'BILLING ₹/MO' : 'BILLING $/HR'}</label>
                <input style={inp} type="number" value={row.billingRate} onChange={e => setRow(i, 'billingRate', e.target.value)} placeholder="Optional" />
              </div>
            </div>

            {results[i] && (
              <div className="ctc-compare-metrics" style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                <div style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Annual Cost</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>{fmt(results[i].ctc, currency)}</div>
                  <div style={{ fontSize: 11, color: isDark ? '#475569' : '#d1d5db', marginTop: 3 }}>{fmt(results[i].ctc / 12, currency)}/mo</div>
                  {/* relative bar */}
                  <div style={{ marginTop: 8, height: 5, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }}>
                    <div style={{ height: '100%', borderRadius: 3, background: COLORS[i % COLORS.length], width: maxCTC > 0 ? `${(results[i].ctc / maxCTC) * 100}%` : '0%', transition: 'width 0.4s' }} />
                  </div>
                </div>
                {results[i].margin && (
                  <>
                    <div style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Annual Billing</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>{fmt(results[i].margin.billingAnnual, currency)}</div>
                      <div style={{ fontSize: 11, color: isDark ? '#475569' : '#d1d5db', marginTop: 3 }}>{fmt(results[i].margin.billingMonthly, currency)}/mo</div>
                    </div>
                    <div style={{ background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Gross Margin</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: results[i].margin.marginPct >= 30 ? '#10b981' : results[i].margin.marginPct >= 15 ? '#f59e0b' : '#f43f5e' }}>
                        {pct(results[i].margin.marginPct)}
                      </div>
                      <div style={{ fontSize: 11, color: isDark ? '#475569' : '#d1d5db', marginTop: 3 }}>{fmt(results[i].margin.grossMargin, currency)} profit</div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}

        {rows.length < 5 && (
          <button
            onClick={addRow}
            style={{ padding: '10px 20px', borderRadius: 10, border: `2px dashed ${isDark ? 'rgba(99,102,241,0.4)' : '#c7d2fe'}`, background: 'transparent', cursor: 'pointer', color: isDark ? '#818cf8' : '#6366f1', fontSize: 13, fontWeight: 600 }}
          >
            + Add Candidate
          </button>
        )}

        {/* Summary table */}
        {results.some(Boolean) && (
          <div style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, borderRadius: 14, padding: 20, overflowX: 'auto' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Comparison Summary</p>
            <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: isDark ? 'rgba(99,102,241,0.12)' : '#f0f4ff' }}>
                  {['#', 'Candidate', 'Annual Cost', 'Monthly Cost', 'Annual Billing', 'Gross Margin', 'Markup %'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const r = results[i];
                  if (!r) return null;
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}` }}>
                      <td style={{ padding: '10px 12px' }}><div style={{ width: 22, height: 22, borderRadius: 6, background: COLORS[i%COLORS.length], color:'#fff', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>{i+1}</div></td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: isDark ? '#f1f5f9' : '#111827', wordBreak: 'break-word' }}>{row.name || `Candidate ${i+1}`}<br/><span style={{ fontSize: 11, fontWeight: 400, color: isDark ? '#64748b' : '#9ca3af', wordBreak: 'break-word' }}>{row.role}</span></td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: isDark ? '#f1f5f9' : '#111827', whiteSpace: 'nowrap' }}>{fmt(r.ctc, currency)}</td>
                      <td style={{ padding: '10px 12px', color: isDark ? '#cbd5e1' : '#374151', whiteSpace: 'nowrap' }}>{fmt(r.ctc/12, currency)}</td>
                      <td style={{ padding: '10px 12px', color: isDark ? '#cbd5e1' : '#374151', whiteSpace: 'nowrap' }}>{r.margin ? fmt(r.margin.billingAnnual, currency) : '—'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, whiteSpace: 'nowrap', color: r.margin ? (r.margin.marginPct >= 30 ? '#10b981' : r.margin.marginPct >= 15 ? '#f59e0b' : '#f43f5e') : (isDark ? '#475569' : '#d1d5db') }}>
                        {r.margin ? pct(r.margin.marginPct) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', color: isDark ? '#cbd5e1' : '#374151', whiteSpace: 'nowrap' }}>{r.margin ? pct(r.margin.markupPct) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function CTCCalculator() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('takehome');
  const [country, setCountry]     = useState('India');

  // Pre-fill from navigation state (e.g. opened from candidate profile)
  useEffect(() => {
    if (location.state?.candidateSalary) {
      setCountry(location.state?.currency === 'USD' ? 'USA' : 'India');
    }
  }, [location.state]);

  const bg  = isDark ? '#0f172a' : '#f8fafc';
  const card = { background: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`, borderRadius: 14 };

  const tabs = [
    { id: 'takehome', label: 'Take-Home Calculator', icon: '🧮' },
    { id: 'margin',   label: 'Billing Margin',        icon: '📊' },
    { id: 'compare',  label: 'Compare Candidates',    icon: '⚖️' },
  ];

  return (
    <div className="ctc-page" style={{ minHeight: '100vh', background: bg, padding: '24px 28px' }}>
      <style>{`
        @keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }
        @media (max-width: 1023px) {
          .ctc-page { padding: 16px !important; }
          .ctc-two-col { grid-template-columns: 1fr !important; }
          .ctc-tabs { width: 100% !important; overflow-x: auto !important; }
          .ctc-compare-row { grid-template-columns: 1fr !important; }
          .ctc-compare-metrics { grid-template-columns: 1fr !important; }
          .ctc-pair { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 640px) and (max-width: 1023px) {
          .ctc-compare-metrics { grid-template-columns: repeat(3, 1fr) !important; }
          .ctc-pair { grid-template-columns: 1fr 1fr !important; }
        }
        @media (min-width: 1024px) and (max-width: 1279px) {
          .ctc-compare-row { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: isDark ? '#f1f5f9' : '#0f172a', margin: 0 }}>💰 CTC Calculator</h1>
          <p style={{ fontSize: 14, color: isDark ? '#64748b' : '#9ca3af', marginTop: 4 }}>Take-home breakdowns, billing margins, and candidate cost comparison</p>
        </div>

        {/* Country switcher */}
        <div style={{ display: 'flex', background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', borderRadius: 10, padding: 3, gap: 2 }}>
          {['India', 'USA'].map(c => (
            <button key={c} onClick={() => setCountry(c)} style={{
              padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
              background: country === c ? (isDark ? '#6366f1' : '#6366f1') : 'transparent',
              color: country === c ? '#fff' : (isDark ? '#94a3b8' : '#6b7280'),
            }}>
              {c === 'India' ? '🇮🇳 India' : '🇺🇸 USA'}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="ctc-tabs" style={{ display: 'flex', gap: 4, marginBottom: 24, background: isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9', borderRadius: 12, padding: 4, width: 'fit-content' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '9px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.2s', whiteSpace: 'nowrap',
            background: activeTab === t.id ? (isDark ? '#1e293b' : '#fff') : 'transparent',
            color: activeTab === t.id ? (isDark ? '#f1f5f9' : '#0f172a') : (isDark ? '#64748b' : '#6b7280'),
            boxShadow: activeTab === t.id ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
            flexShrink: 0,
          }}>
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'takehome' && <TakeHomeTab country={country} isDark={isDark} />}
      {activeTab === 'margin'   && <BillingMarginTab country={country} isDark={isDark} />}
      {activeTab === 'compare'  && <ComparisonTab country={country} isDark={isDark} />}
    </div>
  );
}
